<template>
  <v-container fluid class="pa-0">
    <v-card outlined>
      <v-card-text class="pa-4">
        <v-form>
          <v-row dense>
            <v-col cols="12" sm="5">
              <v-menu
                v-model="startMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="startDate"
                    label="Start Date"
                    prepend-icon="mdi-calendar-start"
                    readonly
                    outlined
                    dense
                    hide-details
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="startDate"
                  @input="startMenu = false"
                  :max="endDate"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="12" sm="5">
              <v-menu
                v-model="endMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="endDate"
                    label="End Date"
                    prepend-icon="mdi-calendar-end"
                    readonly
                    outlined
                    dense
                    hide-details
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="endDate"
                  @input="endMenu = false"
                  :min="startDate"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="12" sm="2" class="d-flex align-center">
              <v-btn
                color="primary"
                :loading="loading"
                :disabled="loading"
                @click="loadData"
                block
              >
                Generate
              </v-btn>
            </v-col>
          </v-row>
        </v-form>

        <div v-if="loading" class="text-center py-12">
          <v-progress-circular indeterminate color="primary" size="64" />
          <div class="mt-4">Loading rent charged data...</div>
        </div>

        <div v-else-if="!pivotData.length" class="text-center py-12 grey--text text--darken-1">
          No rent charged data found for the selected period
        </div>

        <template v-else>
          <v-row class="mt-6 mb-4" align="center">
            <v-col cols="12" md="8">
              <v-row dense>
                <v-col cols="12" sm="6">
                  <v-select
                    v-model="selectedUnit"
                    :items="unitItems"
                    label="Filter by Unit"
                    outlined
                    dense
                    clearable
                    hide-details
                  ></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-btn-toggle v-model="sortMode" mandatory dense>
                    <v-btn small value="reference">Reference</v-btn>
                    <v-btn small value="unit">Unit</v-btn>
                  </v-btn-toggle>
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="12" md="4" class="text-right">
              <v-btn
                color="error"
                dark
                :loading="pdfLoading"
                @click="exportPdf"
                :disabled="!filteredRows.length"
              >
                <v-icon left>mdi-file-pdf-box</v-icon>
                Export PDF
              </v-btn>
            </v-col>
          </v-row>

          <div id="print-area">
            <div class="text-center mb-6">
              <h2 class="mb-2">Rent Charged Report</h2>
              <div class="text-subtitle-1 grey--text">
                Period: {{ formatDate(startDate) }} — {{ formatDate(endDate) }}
              </div>
            </div>

            <div class="table-responsive">
              <table class="rent-table">
                <thead>
                  <tr>
                    <th class="reference-col">REFERENCE</th>
                    <th class="tenant-col">PROPERTY - UNIT - TENANT</th>
                    <th v-for="month in months" :key="month" class="amount-col">
                      {{ month }}
                    </th>
                    <th class="total-col">TOTAL CHARGED</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in filteredRows" :key="row.reference">
                    <td class="reference-col">{{ row.reference }}</td>
                    <td class="tenant-col">
                      {{ row.property }} - Unit {{ row.unit || '?' }} - {{ row.tenantName || 'Unknown' }}
                    </td>
                    <td v-for="month in months" :key="month" class="amount-col text-right">
                      {{ formatCurrency(row.monthlyCharged[month] || 0) }}
                    </td>
                    <td class="total-col text-right font-weight-bold">
                      {{ formatCurrency(row.totalCharged) }}
                    </td>
                  </tr>
                  <tr class="grand-total">
                    <td colspan="2" class="text-left pl-4 font-weight-bold">TOTAL CHARGED</td>
                    <td v-for="month in months" :key="month" class="amount-col text-right font-weight-bold">
                      {{ formatCurrency(monthlyTotals[month]) }}
                    </td>
                    <td class="total-col text-right font-weight-bold">
                      {{ formatCurrency(grandTotal) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </template>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import { collection, getDocs, query, where } from "firebase/firestore";
import { getFirestore } from "firebase/firestore";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const db = getFirestore();

export default {
  name: 'RentChargedReport',
  props: {
    property: Object,
    properties: { type: Array, default: () => [] },
    unit: Object,
    landlord: Object
  },
  data: () => ({
    startDate: null,
    endDate: null,
    startMenu: false,
    endMenu: false,
    loading: false,
    pdfLoading: false,
    pivotData: [], // renamed from flatData/pivotData for clarity
    selectedUnit: null,
    sortMode: 'unit',
    base64logo: '',
    months: [],
    isLandlordMode: false,
    landlordName: '',
    propertyName: ''
  }),

  computed: {
    unitItems() {
      const units = [...new Set(this.pivotData.map(r => r.unit).filter(Boolean))];
      return [
        { text: 'All Units', value: null },
        ...units.sort((a, b) => Number(a) - Number(b)).map(u => ({ text: `Unit ${u}`, value: u }))
      ];
    },
    filteredRows() {
      let result = [...this.pivotData];
      if (this.selectedUnit) {
        result = result.filter(r => r.unit === this.selectedUnit);
      }
      if (this.sortMode === 'unit') {
        result.sort((a, b) => Number(a.unit || 999999) - Number(b.unit || 999999));
      } else {
        result.sort((a, b) => a.reference.localeCompare(b.reference));
      }
      return result;
    },
    monthlyTotals() {
      const totals = {};
      this.months.forEach(m => {
        totals[m] = this.filteredRows.reduce((sum, r) => sum + (r.monthlyCharged[m] || 0), 0);
      });
      return totals;
    },
    grandTotal() {
      return Object.values(this.monthlyTotals).reduce((a, b) => a + b, 0);
    }
  },

  async created() {
    const today = new Date();
    this.endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0];
    this.startDate = new Date(today.getFullYear(), today.getMonth() - 5, 1).toISOString().split('T')[0];
    await this.fetchLogo();
  },

  methods: {
    async fetchLogo() {
      try {
        const q = query(collection(db, "Logo"), where("id", "==", 2));
        const snap = await getDocs(q);
        if (!snap.empty) {
          const data = snap.docs[0].data();
          this.base64logo = (data.logo2 || data.logo || '').replace(/^data:image\/[a-z]+;base64,/, '');
        }
      } catch (e) {
        console.warn("Logo fetch failed", e);
      }
    },

    async loadData() {
      this.loading = true;
      this.pivotData = [];
      this.months = [];

      try {
        const props = this.properties.length ? this.properties : this.property ? [this.property] : [];
        if (!props.length) return;

        this.isLandlordMode = this.properties.length > 1 || (this.properties.length === 1 && this.landlord);
        this.landlordName = this.landlord?.name || '';
        this.propertyName = this.isLandlordMode ? '' : (props[0]?.displayName || 'Unknown Property');

        const start = new Date(this.startDate);
        const end = new Date(this.endDate);
        end.setHours(23, 59, 59, 999);

        const monthSet = new Set();

        for (const p of props) {
          let leaseQ = query(collection(db, "Leases"), where("propertyId", "==", p.id));
          if (this.unit?.unitName) {
            leaseQ = query(leaseQ, where("address", "==", this.unit.unitName));
          }

          const leasesSnap = await getDocs(leaseQ);

          for (const leaseDoc of leasesSnap.docs) {
            const lease = leaseDoc.data();
            const ref = lease.leaseReference || lease.reference || 'UNKNOWN';
            const tenant = lease.tenantName || 'Unknown';
            const unit = lease.address?.includes('Unit')
              ? lease.address.split('Unit')[1]?.trim()
              : lease.unit || null;

            const monthlyCharged = {};

            // You should adjust this part according to your actual data structure
            // Currently using fixed rentalAmount — replace with real monthly charges if available
            const rentalAmount = Number(lease.leaseDetails?.rentalAmount || 0);

            // Simple example — generate months in period
            let current = new Date(Math.max(start, new Date(lease.leaseDetails?.startDate || start)));
            const leaseEnd = new Date(lease.leaseDetails?.endDate || end);

            while (current <= Math.min(end, leaseEnd)) {
              const monthKey = current.toLocaleString('default', { month: 'long', year: 'numeric' });
              monthlyCharged[monthKey] = rentalAmount;
              monthSet.add(monthKey);
              current.setMonth(current.getMonth() + 1);
            }

            const totalCharged = Object.values(monthlyCharged).reduce((a, b) => a + b, 0);

            if (totalCharged > 0) {
              this.pivotData.push({
                reference: ref,
                property: p.displayName || 'Property',
                unit,
                tenantName: tenant,
                monthlyCharged,
                totalCharged
              });
            }
          }
        }

        this.months = [...monthSet].sort((a, b) => new Date(a) - new Date(b));
      } catch (err) {
        console.error("Load data error:", err);
      } finally {
        this.loading = false;
      }
    },

    formatCurrency(val) {
      if (!val || isNaN(val) || val === 0) return '—';
      return 'R ' + Number(val).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    formatDate(date) {
      return date ? new Date(date).toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' }) : '';
    },

    async exportPdf() {
      if (!this.filteredRows.length) return;

      this.pdfLoading = true;

      try {
        const doc = new jsPDF({
          orientation: 'landscape',
          unit: 'pt',
          format: 'a4'
        });

        const pageWidth = doc.internal.pageSize.getWidth();
        const margin = 40;
        let y = 45;

        // Logo
        if (this.base64logo) {
          try {
            const logoSrc = this.base64logo.startsWith('data:')
              ? this.base64logo
              : `data:image/png;base64,${this.base64logo}`;
            const logoWidth = 120;
            const logoHeight = 30;
            const logoX = (pageWidth - logoWidth) / 2;
            doc.addImage(logoSrc, 'PNG', logoX, y, logoWidth, logoHeight);
            y += logoHeight + 20;
          } catch (e) {
            console.warn('Logo failed in PDF', e);
            y += 35;
          }
        }

        // Title
        let headerTitle = "Rent Charged Report";
        if (this.isLandlordMode) {
          headerTitle += " - Multiple Properties";
        } else if (this.propertyName) {
          headerTitle += ` - ${this.propertyName}`;
        }

        doc.setFontSize(19);
        doc.setTextColor(60, 60, 60);
        doc.setFont("helvetica", "bold");
        doc.text(headerTitle, pageWidth / 2, y, { align: 'center' });
        y += 28;

        // Period
        doc.setFontSize(10);
        doc.setTextColor(85, 85, 85);
        doc.setFont("helvetica", "normal");
        doc.text(
          `Period: ${this.formatDate(this.startDate)} — ${this.formatDate(this.endDate)}`,
          pageWidth / 2,
          y,
          { align: 'center' }
        );
        y += 25;

        // Separator
        doc.setLineWidth(0.9);
        doc.setDrawColor(160, 160, 160);
        doc.line(margin, y, pageWidth - margin, y);
        y += 30;

        // Table
        const columns = [
          { header: 'REFERENCE', dataKey: 'reference' },
          { header: 'PROPERTY - UNIT - TENANT', dataKey: 'tenantDesc' },
          ...this.months.map(m => ({ header: m, dataKey: m })),
          { header: 'TOTAL CHARGED', dataKey: 'total' }
        ];

        const body = this.filteredRows.map(r => {
          const row = {
            reference: r.reference || '',
            tenantDesc: `${r.property} - Unit ${r.unit || '?'} - ${r.tenantName || 'Unknown'}`
          };
          this.months.forEach(m => {
            row[m] = r.monthlyCharged[m] || 0;
          });
          row.total = r.totalCharged;
          return row;
        });

        const totalsRow = { reference: 'TOTAL', tenantDesc: '' };
        this.months.forEach(m => {
          totalsRow[m] = this.monthlyTotals[m] || 0;
        });
        totalsRow.total = this.grandTotal;
        body.push(totalsRow);

        autoTable(doc, {
          columns,
          body,
          startY: y,
          theme: 'grid',
          margin: { left: margin, right: margin },
          styles: {
            fontSize: 9,
            cellPadding: 6,
            lineColor: [160, 160, 160],
            lineWidth: 0.4,
            overflow: 'ellipsize'
          },
          headStyles: {
            fillColor: [235, 235, 235],
            textColor: [0, 0, 0],
            fontStyle: 'bold',
            halign: 'center'
          },
          alternateRowStyles: { fillColor: [250, 250, 250] },
          columnStyles: {
            reference: { cellWidth: 80, halign: 'left' },
            tenantDesc: { cellWidth: 220, halign: 'left' },
            total: { cellWidth: 95, fontStyle: 'bold' },
            ...Object.fromEntries(
              this.months.map(m => [m, { cellWidth: 95, halign: 'right', font: 'courier' }])
            )
          },
          didParseCell: (data) => {
            if (data.column.dataKey !== 'reference' && data.column.dataKey !== 'tenantDesc') {
              data.cell.styles.halign = 'right';
            }
            if (data.row.index === body.length - 1) {
              data.cell.styles.fillColor = [210, 230, 245];
              data.cell.styles.fontStyle = 'bold';
            }
          },
          didDrawPage: () => {
            doc.setFontSize(8);
            doc.setTextColor(90);
            doc.text(
              `Page ${doc.internal.getNumberOfPages()} • Generated ${new Date().toLocaleDateString('en-ZA')}`,
              pageWidth - margin - 10,
              doc.internal.pageSize.height - 18,
              { align: 'right' }
            );
          }
        });

        const periodStr = `${this.formatDate(this.startDate).replace(/\//g, '-')}_to_${this.formatDate(this.endDate).replace(/\//g, '-')}`;
        const safeName = this.propertyName ? this.propertyName.replace(/[^a-zA-Z0-9]/g, '_') : 'RentCharged';
        doc.save(`Rent_Charged_${safeName}_${periodStr}.pdf`);

      } catch (err) {
        console.error('PDF generation failed:', err);
        alert('Failed to generate PDF. Please check the console.');
      } finally {
        this.pdfLoading = false;
      }
    }
  }
};
</script>

<style scoped>
.rent-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.92rem;
  table-layout: auto;
}
.rent-table th,
.rent-table td {
  border: 1px solid #ccc;
  padding: 9px 12px;
}
.rent-table th {
  background-color: #f8f9fa;
  font-weight: 600;
  white-space: nowrap;
}
.rent-table .grand-total {
  background-color: #e3f2fd;
  font-weight: bold;
}
.table-responsive {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}
.reference-col { width: 90px; }
.tenant-col { min-width: 320px; }
.amount-col { width: 95px; text-align: right; }
.total-col { width: 110px; text-align: right; font-weight: bold; }
</style>